// =============================================================================
// DEMO DATA — Rich mock data for UI demonstration without a real backend
// =============================================================================

import type { UserProfile, Parcel, ActivityFamily, ActivityTemplate, ActivityInstance, ActivityStatus, DashboardKPIs } from '@/types'

// -----------------------------------------------------------------------------
// Mock authenticated user
// -----------------------------------------------------------------------------
export const DEMO_USER: UserProfile = {
  id:            'demo-user-001',
  company_id:    'cobra-energy',
  role:          'SUPERVISOR_COBRA',
  full_name:     'Antonio Muñoz Chacél',
  phone:         '+34 600 000 000',
  avatar_url:    null,
  is_active:     true,
  last_login_at: new Date().toISOString(),
  created_at:    '2026-01-01T00:00:00Z',
  updated_at:    new Date().toISOString(),
  company: {
    id:            'cobra-energy',
    name:          'Cobra Energía',
    cif:           'A-12345678',
    contact_name:  'Antonio Muñoz',
    contact_email: 'antonio.munoz@cobra.com',
    contact_phone: '+34 91 000 0000',
    is_active:     true,
    created_at:    '2026-01-01T00:00:00Z',
    updated_at:    '2026-01-01T00:00:00Z',
    deleted_at:    null,
  }
}

// -----------------------------------------------------------------------------
// 26 Parcelas with varied progress for demo
// -----------------------------------------------------------------------------
const PARCEL_PROGRESS: Record<number, number> = {
  1: 72, 2: 85, 3: 61, 4: 48, 5: 38,
  6: 55, 7: 90, 8: 43, 9: 67, 10: 30,
  11: 20, 12: 15, 13: 5,  14: 0,  15: 0,
  16: 88, 17: 74, 18: 62, 19: 51, 20: 40,
  21: 95, 22: 78, 23: 33, 24: 12, 25: 60, 26: 45,
}

export const DEMO_PARCELS: Parcel[] = Array.from({ length: 26 }, (_, i) => {
  const num = i + 1
  return {
    id:                       `parc-${String(num).padStart(2, '0')}`,
    parcel_number:            num,
    name:                     `Parc-${String(num).padStart(2, '0')}`,
    display_name:             `Parcela ${num}`,
    has_subestacion:          num === 7 || num === 21,
    has_instalacion_temporal: num === 5 || num === 21 || num === 25,
    total_trackers:           num <= 13 ? 280 : 200,
    geometry:                 null,
    created_at:               '2026-01-01T00:00:00Z',
    updated_at:               '2026-01-01T00:00:00Z',
    progress_pct:             PARCEL_PROGRESS[num] ?? 0,
  }
})

// -----------------------------------------------------------------------------
// Activity families
// -----------------------------------------------------------------------------
export const DEMO_FAMILIES: ActivityFamily[] = [
  { id: 'f01', code: '1',  name: 'Instalaciones temporales', scope_type: 'INSTALACION_TEMPORAL', discipline: 'Instalaciones Temporales', applies_globally: false, per_parcel: false, special_parcels: [5,21,25], display_order: 1, description: null, created_at: '', updated_at: '', subactivity_count: 3 },
  { id: 'f02', code: '2',  name: 'Hincado', scope_type: 'PARCELA', discipline: 'Mecánica', applies_globally: false, per_parcel: true, special_parcels: null, display_order: 2, description: null, created_at: '', updated_at: '', subactivity_count: 6 },
  { id: 'f03', code: '3',  name: 'Montaje de tracker', scope_type: 'PARCELA', discipline: 'Mecánica', applies_globally: false, per_parcel: true, special_parcels: null, display_order: 3, description: null, created_at: '', updated_at: '', subactivity_count: 8 },
  { id: 'f04', code: '4',  name: 'Montaje de módulos', scope_type: 'PARCELA', discipline: 'Módulos', applies_globally: false, per_parcel: true, special_parcels: null, display_order: 4, description: null, created_at: '', updated_at: '', subactivity_count: 2 },
  { id: 'f05', code: '5',  name: 'Zanjas BT', scope_type: 'ZANJA_BT', discipline: 'Eléctrica BT', applies_globally: false, per_parcel: true, special_parcels: null, display_order: 5, description: null, created_at: '', updated_at: '', subactivity_count: 3 },
  { id: 'f06', code: '6',  name: 'Zanjas MT', scope_type: 'ZANJA_MT', discipline: 'Eléctrica MT', applies_globally: false, per_parcel: true, special_parcels: null, display_order: 6, description: null, created_at: '', updated_at: '', subactivity_count: 7 },
  { id: 'f07', code: '7',  name: 'Zanjas 132 kV', scope_type: 'LINEA_132KV', discipline: 'Alta Tensión', applies_globally: true, per_parcel: false, special_parcels: null, display_order: 7, description: null, created_at: '', updated_at: '', subactivity_count: 7 },
  { id: 'f08', code: '8',  name: 'Prisma línea 400 kV', scope_type: 'LINEA_400KV', discipline: 'Líneas Evacuación', applies_globally: true, per_parcel: false, special_parcels: null, display_order: 8, description: null, created_at: '', updated_at: '', subactivity_count: 8 },
  { id: 'f09', code: '9',  name: 'Cámaras empalme y botellas', scope_type: 'CAMARA_EMPALME_BOTELLA', discipline: 'Alta Tensión', applies_globally: true, per_parcel: false, special_parcels: null, display_order: 9, description: null, created_at: '', updated_at: '', subactivity_count: 7 },
  { id: 'f10', code: '10', name: 'Instalación equipos', scope_type: 'PARCELA', discipline: 'Eléctrica BT', applies_globally: false, per_parcel: true, special_parcels: null, display_order: 10, description: null, created_at: '', updated_at: '', subactivity_count: 5 },
  { id: 'f11', code: '11', name: 'Conexionado', scope_type: 'PARCELA', discipline: 'Eléctrica BT', applies_globally: false, per_parcel: true, special_parcels: null, display_order: 11, description: null, created_at: '', updated_at: '', subactivity_count: 9 },
  { id: 'f12', code: '12', name: 'CCTV', scope_type: 'CCTV', discipline: 'CCTV/Seguridad', applies_globally: false, per_parcel: true, special_parcels: null, display_order: 12, description: null, created_at: '', updated_at: '', subactivity_count: 7 },
  { id: 'f13', code: '13', name: 'Perforaciones horizontales', scope_type: 'PERFORACION_HORIZONTAL', discipline: 'Civil', applies_globally: true, per_parcel: false, special_parcels: null, display_order: 13, description: null, created_at: '', updated_at: '', subactivity_count: 5 },
  { id: 'f14', code: '14', name: 'Vallado', scope_type: 'VALLADO', discipline: 'Civil', applies_globally: false, per_parcel: true, special_parcels: null, display_order: 14, description: null, created_at: '', updated_at: '', subactivity_count: 4 },
  { id: 'f15', code: '15', name: 'Viales', scope_type: 'VIAL', discipline: 'Civil', applies_globally: false, per_parcel: true, special_parcels: null, display_order: 15, description: null, created_at: '', updated_at: '', subactivity_count: 6 },
  { id: 'f16', code: '16', name: 'Subestaciones', scope_type: 'SUBESTACION', discipline: 'Subestaciones', applies_globally: false, per_parcel: false, special_parcels: [7,21], display_order: 16, description: null, created_at: '', updated_at: '', subactivity_count: 13 },
  { id: 'f17', code: '17', name: 'Edificios', scope_type: 'EDIFICIO', discipline: 'Edificios', applies_globally: true, per_parcel: false, special_parcels: null, display_order: 17, description: null, created_at: '', updated_at: '', subactivity_count: 7 },
]

// -----------------------------------------------------------------------------
// Demo dashboard KPIs
// -----------------------------------------------------------------------------
export const DEMO_KPIS: DashboardKPIs = {
  global_progress_pct:         47.3,
  total_activity_instances:    462,
  not_started:                 127,
  in_progress:                 198,
  completed:                   112,
  blocked:                     25,
  pending_validations:         34,
  pending_documents:           18,
  rejected_documents:          7,
  parcels_behind_schedule:     9,
  total_trackers:              6895,
  labeled_trackers:            5234,
  unlabeled_trackers:          1661,
  review_queue_count:          43,
}

// -----------------------------------------------------------------------------
// Demo activity instances for table view
// -----------------------------------------------------------------------------
const DEMO_STATUSES: ActivityStatus[] = ['NO_INICIADA', 'EN_CURSO', 'FINALIZADA', 'BLOQUEADA']
const DEMO_COMPANIES = [
  { name: 'Mecano Trackers SL' },
  { name: 'Electra Obras SA' },
  { name: 'Civil Navabuena UTE' },
]

export const DEMO_ACTIVITY_INSTANCES: ActivityInstance[] = [
  ...DEMO_PARCELS.slice(0, 10).flatMap((parcel, pi) =>
    DEMO_FAMILIES.slice(0, 7).map((family, fi) => {
      const seed   = pi * 7 + fi
      const status = DEMO_STATUSES[seed % 4]
      const pct    = status === 'FINALIZADA' ? 100 : status === 'NO_INICIADA' ? 0 : (seed * 13) % 90 + 5
      return {
        id:            `inst-${pi}-${fi}`,
        family_id:     family.id,
        parcel_id:     parcel.id,
        scope_type:    'PARCELA' as const,
        scope_ref:     null,
        status,
        progress_pct:  pct,
        validated_pct: Math.round(pct * 0.8),
        planned_start: '2026-01-15',
        planned_end:   '2026-06-30',
        actual_start:  status !== 'NO_INICIADA' ? '2026-02-01' : null,
        actual_end:    status === 'FINALIZADA' ? '2026-04-01' : null,
        notes:         null,
        created_at:    '2026-01-01T00:00:00Z',
        updated_at:    new Date().toISOString(),
        family,
        parcel,
        // Shape matches ActivityTable's Supabase join: assignments array
        assignments: [{ is_active: true, company: DEMO_COMPANIES[seed % 3] }],
      } as unknown as ActivityInstance
    })
  ),
]

// -----------------------------------------------------------------------------
// Progress by discipline for charts
// -----------------------------------------------------------------------------
export const DEMO_PROGRESS_BY_DISCIPLINE = [
  { discipline: 'Civil',                 not_started: 12, in_progress: 18, completed: 8,  blocked: 2,  total: 40,  pct: 42 },
  { discipline: 'Mecánica',              not_started: 5,  in_progress: 28, completed: 15, blocked: 4,  total: 52,  pct: 55 },
  { discipline: 'Módulos',               not_started: 18, in_progress: 6,  completed: 2,  blocked: 0,  total: 26,  pct: 25 },
  { discipline: 'Eléctrica BT',          not_started: 20, in_progress: 22, completed: 10, blocked: 8,  total: 60,  pct: 38 },
  { discipline: 'Eléctrica MT',          not_started: 15, in_progress: 20, completed: 17, blocked: 3,  total: 55,  pct: 47 },
  { discipline: 'Alta Tensión',          not_started: 2,  in_progress: 3,  completed: 1,  blocked: 0,  total: 6,   pct: 42 },
  { discipline: 'Líneas Evacuación',     not_started: 0,  in_progress: 2,  completed: 0,  blocked: 1,  total: 3,   pct: 30 },
  { discipline: 'Subestaciones',         not_started: 0,  in_progress: 4,  completed: 8,  blocked: 1,  total: 13,  pct: 78 },
  { discipline: 'CCTV/Seguridad',        not_started: 15, in_progress: 8,  completed: 3,  blocked: 0,  total: 26,  pct: 28 },
  { discipline: 'Inst. Temporales',      not_started: 0,  in_progress: 0,  completed: 3,  blocked: 0,  total: 3,   pct: 100 },
  { discipline: 'Edificios',             not_started: 0,  in_progress: 1,  completed: 0,  blocked: 0,  total: 1,   pct: 45 },
]

// -----------------------------------------------------------------------------
// Alerts
// -----------------------------------------------------------------------------
export const DEMO_ALERTS = [
  { id: 'a1', severity: 'high' as const, type: 'BLOCKED', message: 'Parcela 6 – Hincado bloqueado por conflicto topográfico', parcel: 'Parc-06', activity: 'Hincado', created_at: '2026-03-25T10:00:00Z' },
  { id: 'a2', severity: 'high' as const, type: 'PENDING_VALIDATION', message: '12 avances de Mecano Trackers SL pendientes de validación (>3 días)', parcel: 'Parc-03', activity: 'Montaje tracker', created_at: '2026-03-24T08:00:00Z' },
  { id: 'a3', severity: 'medium' as const, type: 'REJECTED_DOC', message: 'PPI Zanjas MT rechazado en Parcela 11 – falta firma supervisor', parcel: 'Parc-11', activity: 'Zanjas MT', created_at: '2026-03-26T14:30:00Z' },
  { id: 'a4', severity: 'medium' as const, type: 'BLOCKED', message: 'Parcela 18 – Conexionado bloqueado, pendiente de material', parcel: 'Parc-18', activity: 'Conexionado', created_at: '2026-03-27T09:15:00Z' },
  { id: 'a5', severity: 'low' as const, type: 'BEHIND_SCHEDULE', message: '9 parcelas por debajo del avance planificado para la semana', parcel: 'Global', activity: '—', created_at: '2026-03-28T07:00:00Z' },
  { id: 'a6', severity: 'low' as const, type: 'PENDING_DOC', message: '18 documentos pendientes de revisión (checklist + PPIs)', parcel: 'Varios', activity: 'Varios', created_at: '2026-03-28T07:00:00Z' },
  { id: 'a7', severity: 'high' as const, type: 'BLOCKED', message: 'SET Parcela 21 – Montaje equipos bloqueado por acopio pendiente', parcel: 'Parc-21', activity: 'Subestaciones', created_at: '2026-03-27T11:00:00Z' },
]
